import { useEffect, useState } from 'preact/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { T } from '@/ui/tokens';
import { Icons, sized } from '@/ui/icons';
import { Spinner } from '@/ui/primitives/Spinner';
import { referralsRepo } from '@/data/repositories/referrals-repo';
import { useAuthInfo } from '@/features/auth/useAuth';

// One key per user, so the prompt is shown at most once per account on a device.
const seenKey = (userId: string) => `boostly.refPrompt.${userId}`;

/**
 * First-visit prompt asking whether the user was referred by someone. Shows once
 * per user, and only while they have no referrer yet (`referred === false`).
 * Applying a code or dismissing both mark it as seen so it never reappears.
 */
export function ReferralPrompt() {
  const auth = useAuthInfo();
  const qc = useQueryClient();
  const user = auth.data?.me;

  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');

  useEffect(() => {
    if (!user?.id || user.referred) return;
    if (localStorage.getItem(seenKey(user.id))) return;
    setOpen(true);
  }, [user?.id, user?.referred]);

  const markSeen = () => { if (user?.id) localStorage.setItem(seenKey(user.id), '1'); };
  const dismiss = () => { markSeen(); setOpen(false); };

  const apply = useMutation({
    mutationFn: () => referralsRepo.redeem(Number(code.trim())),
    onSuccess: () => {
      markSeen();
      qc.invalidateQueries({ queryKey: ['auth', 'info'] });
      qc.invalidateQueries({ queryKey: ['home'] });
      setOpen(false);
    },
  });

  if (!open || !user) return null;

  const valid = /^\d+$/.test(code.trim()) && Number(code.trim()) > 0;

  return (
    <div
      onClick={dismiss}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 460, margin: '0 auto',
          background: T.surface, borderTopLeftRadius: 22, borderTopRightRadius: 22,
          border: `1px solid ${T.line2}`, borderBottom: 'none',
          padding: '22px 20px calc(24px + var(--safe-bottom, 0px))',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: T.accent }}>{sized(Icons.gift, 18)}</span>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>Got a referral code?</div>
          </div>
          <button onClick={dismiss} aria-label="Close" style={{
            width: 28, height: 28, borderRadius: 8, background: 'transparent',
            border: 'none', color: T.textMute, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>{sized(Icons.x, 16)}</button>
        </div>

        <p style={{ margin: '0 0 16px', fontSize: 13, color: T.textDim, lineHeight: 1.55 }}>
          If a friend invited you, enter their referral code so they get credit for your activity.
        </p>

        <input
          value={code}
          inputMode="numeric"
          onInput={(e) => setCode((e.target as HTMLInputElement).value)}
          placeholder="Referral code"
          style={{
            width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 12,
            background: T.surface2, border: `1px solid ${T.line}`, color: T.text,
            fontFamily: T.fontMono, fontSize: 15, outline: 'none', letterSpacing: 1,
          }}
        />

        {apply.isError && (
          <div style={{ marginTop: 10, fontSize: 13, color: T.danger }}>
            {(apply.error as Error).message}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button onClick={dismiss} style={{
            flex: 1, padding: '13px', borderRadius: 12, background: 'transparent',
            color: T.text, border: `1px solid ${T.line2}`,
            fontFamily: T.font, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>Not now</button>
          <button onClick={() => apply.mutate()} disabled={!valid || apply.isPending} style={{
            flex: 1, padding: '13px', borderRadius: 12,
            background: valid ? T.accent : T.surface2,
            color: valid ? T.accentInk : T.textMute,
            border: 'none', fontFamily: T.font, fontSize: 14, fontWeight: 700,
            cursor: valid && !apply.isPending ? 'pointer' : 'not-allowed',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
            {apply.isPending && <Spinner size={14}/>}
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
